import { useCallback, useMemo } from "react";

import {
  type ContentEmailComponentsQuery,
  type ContentEmailsBoolExp,
  useContentEmailComponentsQuery,
} from "src/graphql";

import { brandProfileScopeClause } from "./brand-profile-clause";
import { usePaginatedRows } from "./use-paginated-rows";

const PAGE_SIZE = 40;

type ComponentRow = ContentEmailComponentsQuery["content_emails"][number];

export type ComponentSearchResult = {
  components: ComponentRow[];
  totalCount: number;
  isLoading: boolean;
  isRefreshing: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
};

const buildFilters = (
  search: string,
  componentTypes: string[],
  brandProfileIds: readonly string[] | null | undefined,
): ContentEmailsBoolExp => {
  const clauses: ContentEmailsBoolExp[] = [];
  if (search.trim()) {
    clauses.push({ name: { _ilike: `%${search.trim()}%` } });
  }
  if (componentTypes.length) {
    clauses.push({ component_type: { _in: componentTypes } });
  }
  const brandProfileClause = brandProfileScopeClause(brandProfileIds);
  if (brandProfileClause) {
    clauses.push(brandProfileClause);
  }
  return clauses.length ? { _and: clauses } : {};
};

/**
 * Paginated component search for the picker: name `_ilike` search plus a
 * component-type facet, over the workspace's email components. Pagination
 * semantics live in `usePaginatedRows`.
 */
export const useComponentSearch = ({
  search,
  componentTypes,
  brandProfileIds,
  enabled = true,
}: {
  search: string;
  componentTypes: string[];
  /** Only show components available for one of these brand profiles; null shows every profile's. */
  brandProfileIds?: readonly string[] | null;
  /** Skip the query entirely (e.g. the modal isn't showing this source). */
  enabled?: boolean;
}): ComponentSearchResult => {
  // Derive a stable primitive key from the inputs so callers can pass a fresh
  // array/string literal each render without retriggering the filter memo or
  // the pagination reset.
  const typesKey = componentTypes.join(",");
  const searchKey = search.trim();

  const filters = useMemo(
    () =>
      buildFilters(
        searchKey,
        typesKey ? typesKey.split(",") : [],
        brandProfileIds,
      ),
    [searchKey, typesKey, brandProfileIds],
  );

  const { data, isLoading, isFetching } = useContentEmailComponentsQuery(
    {
      filters,
      orderBy: [{ updated_at: "desc" }],
      limit: PAGE_SIZE,
      offset: 0,
    },
    { enabled },
  );

  const fetchPage = useCallback(
    async (offset: number) => {
      const page = await useContentEmailComponentsQuery.fetcher({
        filters,
        orderBy: [{ updated_at: "desc" }],
        limit: PAGE_SIZE,
        offset,
      })();
      return page.content_emails ?? [];
    },
    [filters],
  );

  const { rows, ...pagination } = usePaginatedRows({
    // NUL-delimited so a search string containing spaces/commas can't collide
    // with another search+facet combination.
    filterKey: `${searchKey}\0${typesKey}\0${brandProfileIds?.join(",") ?? ""}`,
    firstPage: data?.content_emails ?? [],
    totalCount: data?.content_emails_aggregate?.aggregate?.count ?? 0,
    isLoading,
    isFetching,
    fetchPage,
  });

  return { components: rows, ...pagination };
};
